import { Link, Stack } from '@mui/material';
import { Call } from '@mui/icons-material';
import { TableV3, TableV3Sort } from 'common/table';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { mapSourcingCandidateDataToTableColumns } from 'modules/sourcing-shared/tables/mapper/mapSourcingCandidateDataToTableColumns';
import { SourcingCandidateTableColumns } from 'modules/sourcing-shared/tables/types/SourcingCandidateTable';
import { TableV3Filters } from 'common/table/components/TableV3';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getSourcingCandidateFilters } from 'modules/sourcing-shared/tables/filters/getSourcingCandidateFilters';
import { ApolloError } from '@apollo/client';
import { SourcingCandidatesEmptyState } from './states/SourcingCandidatesEmptyState';
import { SourcingRevealNumberLink } from '../components/SourcingRevealNumberLink';
import { SourcingCandidateDetailsDrawer } from '../components/SourcingCandidateDetailsDrawer';
import { getSourcingCandidateTableColumns } from './columns/getSourcingCandidatesColumns';
import { useSearchSourcingCandidatesWithSubscription } from '../hooks/useSearchSourcingCandidatesWithSubscription';
import { useSearchParams } from 'react-router-dom';
import {
  SearchableSourcedCandidateSortableFields,
  SearchableSourcedCandidateSortInput,
  SearchableSortDirection
} from 'generated-graphql/types';

const DEFAULT_PAGE_SIZE = 50;

type Props = {
  organizationId: string;
  candidateBatchId?: string;
  onSubscriptionError?: (error: ApolloError) => void;
};

export const SourcingCandidatesTable: React.FC<Props> = ({
  organizationId,
  candidateBatchId,
  onSubscriptionError
}) => {
  const { openDrawer } = useDrawerV2();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<
    TableV3Filters<SourcingCandidateTableColumns>
  >({});
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [from, setFrom] = useState(0);
  const [sort, setSort] = useState<
    TableV3Sort<SearchableSourcedCandidateSortableFields>
  >({
    field: SearchableSourcedCandidateSortableFields.ConversationRepliesCount,
    direction: 'desc'
  });
  const hasOpenedDrawerRef = useRef(false);

  const currentPage = from / pageSize + 1;

  const handleNext = () => {
    setFrom((prev) => prev + pageSize);
  };

  const handlePrevious = () => {
    setFrom((prev) => Math.max(0, prev - pageSize));
  };

  const queryFilters = getSourcingCandidateFilters({
    organizationId,
    candidateBatchId,
    filters
  });

  const sortInput: SearchableSourcedCandidateSortInput = {
    field: sort.field,
    direction: sort.direction as SearchableSortDirection
  };

  const {
    sourcedCandidates,
    total: totalSourcedCandidates,
    loading,
    subscriptionError
  } = useSearchSourcingCandidatesWithSubscription({
    filter: queryFilters,
    candidateBatchId,
    limit: pageSize,
    from,
    sort: [sortInput]
  });

  useEffect(() => {
    if (totalSourcedCandidates > 0) {
      setTotal(totalSourcedCandidates);
    }
  }, [totalSourcedCandidates]);

  useEffect(() => {
    if (subscriptionError) {
      onSubscriptionError?.(subscriptionError);
    }
  }, [subscriptionError, onSubscriptionError]);

  const handleViewDetails = useCallback(
    (row: SourcingCandidateTableColumns | undefined, index: number) => {
      if (!row) return;

      const candidate = sourcedCandidates.find((c) => c.id === row.id);
      if (!candidate) return;

      openDrawer({
        title: candidate.fullName ?? '',
        subtitle: (
          <Stack direction="row" alignItems="center" spacing={1}>
            {candidate.linkedInProfileUrl && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Call color="primary" fontSize="small" />
                <SourcingRevealNumberLink
                  number={candidate.phoneNumber}
                  fetchPhoneNumberAttempted={
                    candidate.fetchPhoneNumberAttempted || false
                  }
                  sourcedCandidateId={candidate.id}
                  linkedInProfileUrl={candidate.linkedInProfileUrl}
                />
              </Stack>
            )}

            {candidate.email && <span>|</span>}
            {candidate.email && (
              <Link href={`mailto:${candidate.email}`}>{candidate.email}</Link>
            )}
          </Stack>
        ),
        width: '1100px',
        children: (
          <SourcingCandidateDetailsDrawer
            candidateBatchId={candidateBatchId || candidate.candidateBatchId}
            candidate={candidate}
            sourcedCandidates={sourcedCandidates}
            index={index}
            onPageChange={(page) => {
              const candidate = sourcedCandidates[page];
              handleViewDetails(
                mapSourcingCandidateDataToTableColumns([candidate])[0],
                page
              );
            }}
          />
        )
      });
    },
    [sourcedCandidates, openDrawer, candidateBatchId]
  );

  const mappedSourcedCandidates = useMemo(
    () => mapSourcingCandidateDataToTableColumns(sourcedCandidates),
    [sourcedCandidates]
  );

  const columns = useMemo(
    () =>
      getSourcingCandidateTableColumns({
        onViewDetails: handleViewDetails,
        hideCandidateBatchJobTitleColumn: candidateBatchId !== undefined
      }),
    [handleViewDetails, candidateBatchId]
  );

  // Auto-open drawer when sourcedCandidateId is in URL params
  useEffect(() => {
    const sourcedCandidateId = searchParams.get('sourcedCandidateId');
    if (
      !sourcedCandidateId ||
      sourcedCandidates.length === 0 ||
      hasOpenedDrawerRef.current
    ) {
      return;
    }

    const candidateIndex = sourcedCandidates.findIndex(
      (c) => c.id === sourcedCandidateId
    );

    if (candidateIndex !== -1) {
      const mappedCandidate = mappedSourcedCandidates[candidateIndex];
      handleViewDetails(mappedCandidate, candidateIndex);
      hasOpenedDrawerRef.current = true;
    }
  }, [
    sourcedCandidates,
    mappedSourcedCandidates,
    handleViewDetails,
    searchParams
  ]);

  const handleChangePage = (newPage: number) => {
    const currentPageZeroIndexed = currentPage - 1;
    if (newPage > currentPageZeroIndexed) {
      handleNext();
    } else {
      handlePrevious();
    }
  };

  const handleChangePageSize = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
  }, []);

  const hasSearchFields =
    filters.searchFields && Object.keys(filters.searchFields).length > 0;
  const hasFilterValues =
    filters.filterValues && Object.keys(filters.filterValues).length > 0;
  const isFiltering = hasSearchFields || hasFilterValues;

  return (
    <TableV3
      tableId={
        candidateBatchId
          ? 'candidate-batch-sourced-candidates-table'
          : 'sourced-candidates-table'
      }
      columns={columns}
      data={mappedSourcedCandidates}
      onFiltersChanged={(newFilters) => {
        setFilters(newFilters);
        setFrom(0);
      }}
      defaultSort={sort}
      onSort={setSort}
      loading={loading}
      emptyStateComponent={
        <SourcingCandidatesEmptyState
          candidateBatchId={candidateBatchId}
          isFiltering={isFiltering}
        />
      }
      paginationOptions={{
        pageSize,
        totalRows: total,
        currentPage: currentPage - 1,
        onChangePage: handleChangePage,
        onChangePageSize: handleChangePageSize
      }}
    />
  );
};
